import { z } from "zod";
import { baseLeaderboardEntrySchema } from "../leaderboard/leaderboard.js";

export const PHOTO_ROULETTE_LEADERBOARD_SCOPE = {
	GLOBAL: "global",
	WEEKLY: "weekly",
	MONTHLY: "monthly",
	SESSION: "session",
} as const;

export const PHOTO_ROULETTE_LEADERBOARD_SCOPE_VALUES = [
	PHOTO_ROULETTE_LEADERBOARD_SCOPE.GLOBAL,
	PHOTO_ROULETTE_LEADERBOARD_SCOPE.WEEKLY,
	PHOTO_ROULETTE_LEADERBOARD_SCOPE.MONTHLY,
	PHOTO_ROULETTE_LEADERBOARD_SCOPE.SESSION,
] as const;

export const PHOTO_ROULETTE_PERSISTENT_LEADERBOARD_SCOPE_VALUES = [
	PHOTO_ROULETTE_LEADERBOARD_SCOPE.GLOBAL,
	PHOTO_ROULETTE_LEADERBOARD_SCOPE.WEEKLY,
	PHOTO_ROULETTE_LEADERBOARD_SCOPE.MONTHLY,
] as const;

export const photoRouletteLeaderboardScopeSchema = z.enum(PHOTO_ROULETTE_LEADERBOARD_SCOPE_VALUES);
export type PhotoRouletteLeaderboardScope = z.infer<typeof photoRouletteLeaderboardScopeSchema>;

export const photoRoulettePersistentLeaderboardScopeSchema = z.enum(PHOTO_ROULETTE_PERSISTENT_LEADERBOARD_SCOPE_VALUES);
export type PhotoRoulettePersistentLeaderboardScope = z.infer<typeof photoRoulettePersistentLeaderboardScopeSchema>;

export const photoRouletteLeaderboardSeasonSchema = z.object({
	scope: z.enum([PHOTO_ROULETTE_LEADERBOARD_SCOPE.WEEKLY, PHOTO_ROULETTE_LEADERBOARD_SCOPE.MONTHLY]),
	key: z.string().min(1),
	starts_at: z.number().int().positive(),
	ends_at: z.number().int().positive(),
});

export type PhotoRouletteLeaderboardSeason = z.infer<typeof photoRouletteLeaderboardSeasonSchema>;

export const photoRouletteLeaderboardEntrySchema = baseLeaderboardEntrySchema.extend({
	total_score: z.number().int().nonnegative(),
	season_score: z.number().int().nonnegative(),
	session_score: z.number().int().nonnegative(),
	correct_answers: z.number().int().nonnegative(),
	average_response_time: z.number().nonnegative().nullable(),
	best_streak: z.number().int().nonnegative(),
	current_streak: z.number().int().nonnegative(),
	score_reached_at: z.number().int().positive().nullable(),
});

export type PhotoRouletteLeaderboardEntry = z.infer<typeof photoRouletteLeaderboardEntrySchema>;

export const photoRouletteLeaderboardResponseSchema = z.object({
	scope: photoRoulettePersistentLeaderboardScopeSchema,
	season: photoRouletteLeaderboardSeasonSchema.nullable(),
	entries: z.array(photoRouletteLeaderboardEntrySchema),
	current_user_entry: photoRouletteLeaderboardEntrySchema.nullable(),
	generated_at: z.number().int().positive(),
});

export type PhotoRouletteLeaderboardResponse = z.infer<typeof photoRouletteLeaderboardResponseSchema>;

export const photoRouletteLeaderboardQuerySchema = z.object({
	scope: photoRoulettePersistentLeaderboardScopeSchema,
	limit: z.coerce.number().int().min(1).max(100).default(25),
});

export type PhotoRouletteLeaderboardQuery = z.infer<typeof photoRouletteLeaderboardQuerySchema>;

const normalizeAverageResponseTime = (value: number | null): number => value ?? Number.POSITIVE_INFINITY;
const normalizeTimestamp = (value: number | null): number => value ?? Number.POSITIVE_INFINITY;

export const comparePhotoRouletteLeaderboardEntries = (
	left: Pick<PhotoRouletteLeaderboardEntry, "score" | "average_response_time" | "correct_answers" | "score_reached_at" | "user_id">,
	right: Pick<PhotoRouletteLeaderboardEntry, "score" | "average_response_time" | "correct_answers" | "score_reached_at" | "user_id">,
): number => {
	if (left.score !== right.score) return right.score - left.score;
	const averageDiff = normalizeAverageResponseTime(left.average_response_time) - normalizeAverageResponseTime(right.average_response_time);
	if (averageDiff !== 0) return averageDiff;
	if (left.correct_answers !== right.correct_answers) return right.correct_answers - left.correct_answers;
	const reachedDiff = normalizeTimestamp(left.score_reached_at) - normalizeTimestamp(right.score_reached_at);
	if (reachedDiff !== 0) return reachedDiff;
	return left.user_id.localeCompare(right.user_id);
};

export const rankPhotoRouletteLeaderboardEntries = (
	entries: readonly Omit<PhotoRouletteLeaderboardEntry, "rank">[],
): PhotoRouletteLeaderboardEntry[] =>
	entries
		.map((entry) => ({ ...entry }))
		.sort(comparePhotoRouletteLeaderboardEntries)
		.map((entry, index) => ({ ...entry, rank: index + 1 }));
